import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAuth, initializeRecaptchaConfig, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore, initializeFirestore } from 'firebase/firestore'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check'

let _app: FirebaseApp | null = null
let _auth: Auth | null = null
let _db: Firestore | null = null
let _appCheck: AppCheck | null = null
let _appCheckInitialized = false
let _recaptchaConfigPromise: Promise<void> | null = null

function requireEnv(name: keyof ImportMetaEnv): string {
  const v = import.meta.env[name]
  if (!v) throw new Error(`Missing required env: ${String(name)}`)
  return v
}

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === 'letmein.local' ||
    host.endsWith('.letmein.local')
  )
}

function initAppCheck(app: FirebaseApp) {
  if (_appCheckInitialized) return
  _appCheckInitialized = true

  const siteKey = (import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY || '').trim()
  const onLocalDev = isLocalDevHost()
  const debugEnabled = (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG || '').toLowerCase() === 'true'
  const debugToken = (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN || '').trim()

  if (!siteKey) {
    if (onLocalDev && debugEnabled) {
      console.warn(
        '[Firebase App Check] VITE_FIREBASE_APPCHECK_SITE_KEY is missing. ' +
          'Copy the reCAPTCHA Enterprise site key from Firebase Console → App Check → your web app, ' +
          'add it to .env, and restart the dev server. Debug token alone is not enough to initialize App Check.',
      )
    }
    return
  }

  // Redundant with firebaseAppCheckBootstrap.ts, but kept for non-Vite entry points.
  if (onLocalDev && debugEnabled) {
    ;(globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken || true
    if (debugToken) {
      console.info('[Firebase App Check] Debug token active for local dev.')
    } else {
      console.info(
        '[Firebase App Check] Set VITE_FIREBASE_APPCHECK_DEBUG_TOKEN in .env and register it in ' +
          'Firebase Console → App Check → Manage debug tokens.',
      )
    }
  } else if (onLocalDev && !debugEnabled) {
    console.warn(
      '[Firebase App Check] Local dev host without debug mode — reCAPTCHA Enterprise will fail. ' +
        'Set VITE_FIREBASE_APPCHECK_DEBUG=true or remove VITE_FIREBASE_APPCHECK_SITE_KEY locally.',
    )
    return
  }

  const providerRaw = (import.meta.env.VITE_FIREBASE_APPCHECK_PROVIDER || 'enterprise').toLowerCase()
  const provider =
    providerRaw === 'v3' || providerRaw === 'recaptchav3'
      ? new ReCaptchaV3Provider(siteKey)
      : new ReCaptchaEnterpriseProvider(siteKey)

  _appCheck = initializeAppCheck(app, {
    provider,
    isTokenAutoRefreshEnabled: true,
  })
}

/**
 * Preload Identity Platform reCAPTCHA config before phone auth.
 * Required when Auth uses reCAPTCHA Enterprise alongside App Check Enterprise.
 */
export function ensureAuthRecaptchaConfig(): Promise<void> {
  if (!_recaptchaConfigPromise) {
    const auth = getFirebaseAuth()
    _recaptchaConfigPromise = initializeRecaptchaConfig(auth).catch((error) => {
      _recaptchaConfigPromise = null
      console.warn('[Firebase Auth] initializeRecaptchaConfig failed:', error)
      throw error
    })
  }
  return _recaptchaConfigPromise
}

export function getFirebaseAuth(): Auth {
  if (_auth) return _auth

  const firebaseConfig = {
    apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
    authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
    storageBucket: requireEnv('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: requireEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: requireEnv('VITE_FIREBASE_APP_ID'),
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  }

  _app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig)
  initAppCheck(_app)
  _auth = getAuth(_app)

  return _auth
}

/**
 * Get the current Firebase App Check token.
 * Returns null if App Check is not initialized or token cannot be retrieved.
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!_appCheck) {
    // Try to initialize if not already done
    const auth = getFirebaseAuth()
    if (!_appCheck) {
      return null
    }
  }

  try {
    const { getToken } = await import('firebase/app-check')
    const result = await getToken(_appCheck!)
    return result.token
  } catch (error) {
    console.warn('Failed to get App Check token:', error)
    return null
  }
}

/**
 * Get the Firestore instance for messaging.
 */
export function getFirebaseFirestore(): Firestore {
  if (_db) return _db

  // Ensure app is initialized
  if (!_app) {
    getFirebaseAuth() // This initializes the app
  }

  try {
    // Force long polling to fix hanging writes on some networks/localhost
    // AND connect to the correct named database 'letmein-db'
    _db = initializeFirestore(_app!, {
      experimentalForceLongPolling: true,
      experimentalAutoDetectLongPolling: false  // Force it, don't auto-detect
    }, 'letmein-db');
  } catch (e: any) {
    // If already initialized, use the existing instance
    console.warn('⚠️ Firebase: Firestore already initialized, using existing instance:', e.message);
    // Note: getFirestore doesn't support passing databaseId in the same way if already init.
    // We hope the first init worked.
    _db = getFirestore(_app!, 'letmein-db');
  }

  return _db
}

/** Firebase authDomain from build-time env (e.g. auth-stg.letmein.ai). */
export function getConfiguredAuthDomain(): string {
  return (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '').trim()
}

/**
 * True when the app is served from a different host than authDomain.
 * Popup OAuth is unreliable in that case; use signInWithRedirect instead.
 */
export function usesSeparateAuthDomain(): boolean {
  if (typeof window === 'undefined') return false
  const authDomain = getConfiguredAuthDomain()
  if (!authDomain) return false
  return window.location.hostname !== authDomain
}
